/* @jsxImportSource @emotion/react */
import React from 'react'
import classNames from 'classnames'
import scrollIntoView from 'scroll-into-view-if-needed'

import { fontSize } from './constants'
import type { ArticleHeading } from './types'

// On desktop the sidebar card caps the height; the list scrolls inside it.
const listCss = {
  listStyle: 'none',
  minHeight: 0,
  overflowY: 'auto',
  '@media screen and (max-width: 68rem)': { maxHeight: '40vh' },
} as const

const linkCss = {
  padding: '8px 14px',
  fontSize: fontSize.small,
  lineHeight: 1.4,
  transition: 'background .2s cubic-bezier(.215,.61,.355,1)',
} as const

function prefersReducedMotion() {
  return (
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  )
}

export function ArticleToc(props: {
  headings: ArticleHeading[]
  activeId: string | null
  labelledBy: string
}) {
  const listRef = React.useRef<HTMLOListElement>(null)

  // Bounded to the list: on mobile the TOC is in the page flow, and scrolling
  // the window would yank the reader back up to it.
  const { activeId } = props
  React.useEffect(() => {
    const list = listRef.current
    const activeLink = Array.from(
      list?.querySelectorAll<HTMLAnchorElement>('a') ?? []
    ).find((link) => link.getAttribute('href') === `#${activeId}`)
    if (!list || !activeLink) {
      return
    }
    scrollIntoView(activeLink, {
      scrollMode: 'if-needed',
      block: 'center',
      boundary: list,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    })
  }, [activeId])

  return (
    <nav
      aria-labelledby={props.labelledBy}
      className="flexbox flexbox-column"
      css={{ minHeight: 0 }}
    >
      <ol ref={listRef} css={listCss}>
        {props.headings.map((heading) => {
          const isActive = heading.id === activeId
          return (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                aria-current={isActive ? 'location' : undefined}
                className={classNames(
                  'block radius-large margin-bottom-2 ellipsis font-weight-semibold',
                  isActive
                    ? 'shade-00 background-alpha-shade-50'
                    : 'shade-05 shade-05-on-hover background-alpha-shade-50-on-hover'
                )}
                css={linkCss}
                onClick={(event) => {
                  const element = document.getElementById(heading.id)
                  if (!element) {
                    return
                  }
                  event.preventDefault()
                  // Native: the library applies scroll-margin-top twice and
                  // skips headings that are already on screen.
                  element.scrollIntoView({
                    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
                    block: 'start',
                  })
                  window.history.replaceState(null, '', `#${heading.id}`)
                }}
              >
                {heading.title}
              </a>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
